export interface DefaultTextLayerConfig {
  text: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  fontStyle: string;
  textDecoration: string;
  textAlign: string;
  color: string;
  outlineColor: string;
  outlineWidth: number;
  letterSpacing: number;
  lineHeight: number;
  opacity: number;
  source?: string;
}

export const DEFAULT_TEXT_LAYER: DefaultTextLayerConfig = {
  text: 'Add text',
  fontFamily: 'Arial',
  fontSize: 24,
  fontWeight: 'normal',
  fontStyle: 'normal',
  textDecoration: 'none',
  textAlign: 'center',
  color: '#000000',
  outlineColor: '#000000',
  outlineWidth: 0,
  letterSpacing: 0,
  lineHeight: 1.2,
  opacity: 1,
};

const FONT_WEIGHTS = ['normal', 'bold', '100', '200', '300', '400', '500', '600', '700', '800', '900'];
const FONT_STYLES = ['normal', 'italic', 'oblique'];
const TEXT_DECORATIONS = ['none', 'underline', 'line-through', 'overline'];
const TEXT_ALIGNS = ['left', 'center', 'right', 'justify'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function pick<T>(data: Record<string, unknown>, key: string, fallback: T, aliases: string[] = []): unknown | T {
  if (key in data) return data[key];
  for (const alias of aliases) {
    if (alias in data) return data[alias];
  }
  return fallback;
}

function stringValue(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function numberValue(value: unknown, fallback: number, min: number, max: number): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

function choice(value: unknown, allowed: string[], fallback: string): string {
  const v = String(value ?? '');
  return allowed.includes(v) ? v : fallback;
}

function color(value: unknown, fallback: string): string {
  const v = String(value ?? '').trim();
  return /^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(v) ? v : fallback;
}

export function sanitizeDefaultTextLayer(raw: unknown): DefaultTextLayerConfig {
  let data: unknown = raw;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      data = {};
    }
  }
  const record = isRecord(data) ? data : {};

  return {
    text: stringValue(pick(record, 'text', DEFAULT_TEXT_LAYER.text), DEFAULT_TEXT_LAYER.text).trim() || DEFAULT_TEXT_LAYER.text,
    fontFamily: stringValue(pick(record, 'fontFamily', DEFAULT_TEXT_LAYER.fontFamily, ['font_family']), DEFAULT_TEXT_LAYER.fontFamily).trim() || DEFAULT_TEXT_LAYER.fontFamily,
    fontSize: Math.round(numberValue(pick(record, 'fontSize', DEFAULT_TEXT_LAYER.fontSize, ['font_size']), DEFAULT_TEXT_LAYER.fontSize, 6, 300)),
    fontWeight: choice(pick(record, 'fontWeight', DEFAULT_TEXT_LAYER.fontWeight, ['font_weight']), FONT_WEIGHTS, DEFAULT_TEXT_LAYER.fontWeight),
    fontStyle: choice(pick(record, 'fontStyle', DEFAULT_TEXT_LAYER.fontStyle, ['font_style']), FONT_STYLES, DEFAULT_TEXT_LAYER.fontStyle),
    textDecoration: choice(pick(record, 'textDecoration', DEFAULT_TEXT_LAYER.textDecoration, ['text_decoration']), TEXT_DECORATIONS, DEFAULT_TEXT_LAYER.textDecoration),
    textAlign: choice(pick(record, 'textAlign', DEFAULT_TEXT_LAYER.textAlign, ['text_align']), TEXT_ALIGNS, DEFAULT_TEXT_LAYER.textAlign),
    color: color(pick(record, 'color', DEFAULT_TEXT_LAYER.color), DEFAULT_TEXT_LAYER.color),
    outlineColor: color(pick(record, 'outlineColor', DEFAULT_TEXT_LAYER.outlineColor, ['outline_color']), DEFAULT_TEXT_LAYER.outlineColor),
    outlineWidth: numberValue(pick(record, 'outlineWidth', DEFAULT_TEXT_LAYER.outlineWidth, ['outline_width']), DEFAULT_TEXT_LAYER.outlineWidth, 0, 20),
    letterSpacing: numberValue(pick(record, 'letterSpacing', DEFAULT_TEXT_LAYER.letterSpacing, ['letter_spacing']), DEFAULT_TEXT_LAYER.letterSpacing, -20, 50),
    lineHeight: numberValue(pick(record, 'lineHeight', DEFAULT_TEXT_LAYER.lineHeight, ['line_height']), DEFAULT_TEXT_LAYER.lineHeight, 0.5, 5),
    opacity: numberValue(pick(record, 'opacity', DEFAULT_TEXT_LAYER.opacity), DEFAULT_TEXT_LAYER.opacity, 0, 1),
    source: stringValue(pick(record, 'source', ''), '') || undefined,
  };
}

export function applyConfig(target: DefaultTextLayerConfig, source: DefaultTextLayerConfig): void {
  Object.assign(target, sanitizeDefaultTextLayer(source));
}
